import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ViewGoalCommitment } from '../../../core';
import { SortOrder } from '../../../grid';
import { CommitmentDetailsDialogColumnId } from '../../model/commitment-details-dialog/commitment-details-dialog-column-id.model';

import { TranslationKeyMap } from './commitment-details-dialog.component.i18n';

@Injectable()
export class CommitmentDetailsSorter {
    columnId: CommitmentDetailsDialogColumnId;
    sortOrder: SortOrder;
    goalCommitments: ViewGoalCommitment[];

    constructor(private readonly _translateService: TranslateService) {
        this.sortOrder = SortOrder.ASC;
    }

    sortColumn(
        columnId: CommitmentDetailsDialogColumnId,
        goalCmts: ViewGoalCommitment[],
        sortOrder: SortOrder | null,
    ): void {
        this._setSortOrder(columnId, sortOrder);
        this.columnId = columnId;

        this.goalCommitments = [...goalCmts].sort((a, b) => {
            let result = this._sort(a, b, columnId, this.sortOrder);
            if (
                result === 0 &&
                columnId === CommitmentDetailsDialogColumnId.BEHAVIOUR
            ) {
                result = this._sort(
                    a,
                    b,
                    CommitmentDetailsDialogColumnId.SUB_BEHAVIOUR,
                    this.sortOrder,
                );
            }
            if (result === 0) {
                // fallback sort by name asc
                result = this._sort(
                    a,
                    b,
                    CommitmentDetailsDialogColumnId.DIRECT_REPORT,
                    this.sortOrder,
                );
                result = this._sort(
                    a,
                    b,
                    CommitmentDetailsDialogColumnId.START_DATE,
                    this.sortOrder,
                );
            }
            return result;
        });
    }

    private _sort(
        a: ViewGoalCommitment,
        b: ViewGoalCommitment,
        columnId: CommitmentDetailsDialogColumnId,
        sortOrder: SortOrder,
    ): number {
        const av = this._dataForColumn(columnId, a);
        const bv = this._dataForColumn(columnId, b);
        return this._sortColumn(av.value, bv.value, av.type, sortOrder);
    }

    private _setSortOrder(
        columnId: CommitmentDetailsDialogColumnId,
        sortOrder: SortOrder | null,
    ): void {
        if (sortOrder != null) {
            this.sortOrder = sortOrder;
            return;
        }
        this.sortOrder =
            this.columnId !== columnId
                ? SortOrder.ASC
                : this.sortOrder === SortOrder.ASC
                ? SortOrder.DESC
                : SortOrder.ASC;
    }

    private _dataForColumn(
        columnId: CommitmentDetailsDialogColumnId,
        goalCmt: ViewGoalCommitment,
    ): {
        value: number | string | null;
        type: 'string' | 'number';
    } {
        switch (columnId) {
            case CommitmentDetailsDialogColumnId.DIRECT_REPORT: {
                return { value: goalCmt.user.firstName, type: 'string' };
            }
            case CommitmentDetailsDialogColumnId.START_DATE: {
                return {
                    value: goalCmt.data.goalStartDate.getTime(),
                    type: 'number',
                };
            }
            case CommitmentDetailsDialogColumnId.DAYS_ACTIVE: {
                return { value: goalCmt.data.ageGoal, type: 'number' };
            }
            case CommitmentDetailsDialogColumnId.METRIC: {
                return { value: goalCmt.metric.fullName, type: 'string' };
            }
            case CommitmentDetailsDialogColumnId.BEHAVIOUR: {
                return { value: goalCmt.behaviour.name, type: 'string' };
            }
            case CommitmentDetailsDialogColumnId.SUB_BEHAVIOUR: {
                return { value: goalCmt.subBehaviour.name, type: 'string' };
            }
            case CommitmentDetailsDialogColumnId.STATE: {
                let val =
                    goalCmt.data.winning > 0
                        ? TranslationKeyMap._10085_WINNING
                        : goalCmt.data.neutral > 0
                        ? TranslationKeyMap._11154_IMPROVING
                        : goalCmt.data.losing > 0
                        ? TranslationKeyMap._11155_DECLINING
                        : null;
                if (val != null) {
                    val = this._translateService.instant(val);
                }
                return {
                    value: val,
                    type: 'string',
                };
            }
            case CommitmentDetailsDialogColumnId.IMPROVEMENT_PERCENTAGE: {
                return {
                    value: goalCmt.data.percentageChangeVariance,
                    type: 'number',
                };
            }
        }
    }

    private _sortColumn(
        valA: string | number | any,
        valB: string | number | any,
        type: 'string' | 'number',
        order: SortOrder,
    ): number {
        if (type === 'number') {
            valA = valA == null ? Number.MIN_SAFE_INTEGER : valA;
            valB = valB == null ? Number.MIN_SAFE_INTEGER : valB;
        } else {
            valA = valA == null ? '' : valA;
            valB = valB == null ? '' : valB;
        }
        return type === 'number'
            ? order === SortOrder.ASC
                ? valA - valB
                : valB - valA
            : order === SortOrder.ASC
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
    }
}
